import {
  faArrowsRotate,
  faSignature,
  faXmark,
} from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DateTime } from 'luxon';
import { useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { cn } from '../../utils/classUtils';

interface AffiliatedBusinessArrangementModalProps {
  onClose: () => void;
  showSignature?: boolean;
  onAcknowledge?: (data: any) => void;
  user?: {
    firstName: string;
    lastName: string;
  };
  isLoading?: boolean;
}

const settlementServices = [
  {
    service: 'Loan Origination Fee',
    charges: '0% - 3% of the loan amount',
  },
  {
    service: 'Credit Report Fee',
    charges: '$0 - $300',
  },
  {
    service: 'Verification Of Employment',
    charges: '$0 - $200',
  },
];

const AffiliatedBusinessArrangementModal: React.FC<
  AffiliatedBusinessArrangementModalProps
> = ({ onClose, showSignature = true, onAcknowledge, user, isLoading }) => {
  const today = useMemo(() => DateTime.now().toFormat('dd/MM/yyyy'), []);
  const fullName = useMemo(
    () => `${user?.firstName} ${user?.lastName}`,
    [user],
  );

  const {
    register,
    watch,
    formState: { isValid },
    handleSubmit,
  } = useForm({
    defaultValues: {
      signature: '',
      date: today,
    },
  });

  const signature = watch('signature');
  const isSubmitDisabled = useMemo(
    () => !isValid || !signature || isLoading,
    [isValid, signature, isLoading],
  );

  return (
    <div className='fixed inset-0 bg-black bg-opacity-60 z-50'>
      <div className='fixed inset-0 z-50 flex items-center justify-center p-4'>
        <div className='bg-white rounded-lg shadow-2xl lg:w-[700px] w-full max-h-[90vh] overflow-hidden'>
          <div className='flex items-center justify-between px-6 py-4 bg-blue-100'>
            <div className='flex items-center gap-2'>
              <FontAwesomeIcon icon={faSignature} size='xl' />
              <h2 className='font-inter-medium text-base text-primary-dark'>
                Affiliated Business Arrangement Disclosure Statement
              </h2>
            </div>
            <button
              onClick={onClose}
              className='text-white bg-reskin-grey-350 rounded-full w-6 h-6 flex items-center justify-center'
            >
              <FontAwesomeIcon icon={faXmark} size='sm' />
            </button>
          </div>

          <div className='px-8 py-6 overflow-y-auto max-h-[calc(90vh-80px)]'>
            <div className='mb-6'>
              <p className='font-inter-regular text-gray-800 text-sm '>
                THIS FORM HAS NOT BEEN APPROVED BY THE COLORADO REAL ESTATE
                COMMISSION. IT HAS BEEN DEVELOPED BY FRASCONA, JOINER, GOODMAN
                AND GREENSTEIN, P.C., AS LEGAL COUNSEL FOR REAL BROKER, LLC.
              </p>
            </div>
            <div className='grid grid-cols-2 gap-x-16 mb-3'>
              <div className='space-y-1'>
                <p className='text-gray-900 text-sm font-inter-regular'>
                  <span className='!font-inter-semibold'>To:</span>{' '}
                  {!showSignature ? 'Potential Customer' : fullName}
                </p>
                <p className='text-gray-900 text-sm font-inter-regular'>
                  <span className='!font-inter-semibold'>From:</span> Real
                  Broker (as defined below)
                </p>
              </div>
              <div className='space-y-1'>
                <p className='text-gray-900 text-sm font-inter-regular'>
                  <span className='!font-inter-semibold'>Property:</span> TBD
                </p>
                <p className='text-gray-900 text-sm font-inter-regular'>
                  <span className='!font-inter-semibold'>Date:</span> {today}
                </p>
              </div>
            </div>

            <div className='space-y-4 text-gray-800 text-sm leading-relaxed'>
              <p className='font-inter-regular'>
                This is to give you notice that{' '}
                <span className='font-inter-bold'>Real Broker</span>, as defined
                below, has a business relationship with the entities listed
                below. The nature of the business relationship is that these
                entities are corporate affiliates. Specifically,{' '}
                <span className='font-inter-bold'>The Real Brokerage</span> Inc.
                owns, directly or indirectly, a 100% interest in Real Broker,
                LLC; Real Broker Alaska, LLC; Real Broker AZ, LLC; Real Broker
                MA, LLC; Real Broker NE, LLC; Real Broker NH, LLC; Real Broker
                NY, LLC; Real Brokerage Technologies, Real of California, and
                Real Broker Commercial, LLC; (collectively &quot;
                <span className='font-inter-bold'>Real Broker</span>&quot;).
                Additionally,{' '}
                <span className='font-inter-bold'>The Real Brokerage Inc.</span>{' '}
                owns directly or indirectly, a 100% interest in{' '}
                <span className='font-inter-bold'>One Real Mortgage Corp.</span>
                The companies are distinct, and an employee or agent of one
                shall not be deemed an employee or agent of the others. Because
                of this relationship, your selection of{' '}
                <span className='font-inter-bold'>
                  One Real Mortgage Corp.
                </span>{' '}
                may provide Real Broker a financial or other benefit.
              </p>

              <p className='font-inter-regular'>
                Set forth below is the estimated charge or range of charges for
                the settlement services listed. You are{' '}
                <span className='font-inter-bold'>NOT required</span> to use{' '}
                <span className='font-inter-bold'>One Real Mortgage Corp.</span>{' '}
                as a condition for settlement of your loan or the purchase,
                sale, or refinance of the subject property.{' '}
                <span className='font-inter-bold'>
                  THERE ARE FREQUENTLY OTHER SETTLEMENT SERVICE PROVIDERS
                  AVAILABLE WITH SIMILAR SERVICES. YOU ARE FREE TO SHOP AROUND
                  TO DETERMINE THAT YOU ARE RECEIVING THE BEST SERVICES AND THE
                  BEST RATE FOR THESE SERVICES.
                </span>
              </p>

              <p className='font-inter-bold mt-6 mb-3 text-sm'>
                One Real Mortgage Corp.
              </p>

              <div className='bg-reskin-grey-200 rounded-lg border border-silver'>
                <table className='w-full rounded-lg'>
                  <thead>
                    <tr className='border-gray-300 bg-gray-2'>
                      <th className='text-left px-4 py-2 font-inter-semibold text-gray-900 rounded-tl-lg'>
                        Settlement Service
                      </th>
                      <th className='text-left px-4 py-2 font-inter-semibold text-gray-900 rounded-tr-lg'>
                        Range of Charges
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {settlementServices.map((item, index) => (
                      <tr
                        key={item.service}
                        className={cn(
                          'bg-gray-1',
                          index < settlementServices.length - 1 &&
                            'border-b border-gray-200',
                        )}
                      >
                        <td
                          className={cn(
                            'px-4 py-2 text-gray-800 font-inter-regular text-sm',
                            index === settlementServices.length - 1 &&
                              'rounded-bl-lg',
                          )}
                        >
                          {item.service}
                        </td>
                        <td
                          className={cn(
                            'px-4 py-2 text-gray-800 font-inter-regular text-sm',
                            index === settlementServices.length - 1 &&
                              'rounded-br-lg',
                          )}
                        >
                          {item.charges}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {showSignature && (
              <>
                <div className='bg-light-blue-100 rounded-lg mt-6 py-3 px-4'>
                  <h2 className='text-sm font-inter-semibold'>
                    ACKNOWLEDGMENT
                  </h2>
                  <p className='text-sm font-inter-regular text-dark mt-2'>
                    I/we have read this disclosure form and understand that Real
                    Broker is referring me/us to purchase the above-described
                    settlement services from One Real Mortgage Corp. and may
                    receive a financial or other benefit as the result of this
                    referral.
                  </p>
                </div>

                <div className='border-t border-gray-200 mt-6 mb-2' />

                <div className='grid grid-cols-2 gap-4 items-center'>
                  <div>
                    <label
                      htmlFor='signature'
                      className='text-sm font-inter-regular text-dark'
                    >
                      Your Full Name (Digital Signature)
                    </label>
                    <input
                      type='text'
                      className='w-full border border-gray-200 rounded-md px-3 py-3 mt-2 outline-none'
                      placeholder='Enter your full name'
                      {...register('signature', { required: true })}
                    />
                  </div>
                  <div>
                    <label
                      htmlFor='date'
                      className='text-sm font-inter-regular text-dark'
                    >
                      Date
                    </label>
                    <input
                      type='text'
                      className='w-full border border-gray-200 rounded-md px-3 py-3 mt-2 text-reskin-grey-500'
                      placeholder='Date'
                      value={today}
                      {...register('date', { value: today })}
                      disabled
                    />
                  </div>
                </div>

                <div className='grid grid-cols-2 gap-4 mt-4'>
                  <button
                    className='rounded-lg py-3 border border-real-grey-200 text-gray-900'
                    onClick={onClose}
                  >
                    Cancel
                  </button>
                  <button
                    className={cn(
                      'rounded-lg py-3 bg-reskin-primary-blue text-white',
                      'disabled:cursor-not-allowed disabled:bg-real-grey-100 disabled:text-real-grey-300',
                    )}
                    disabled={isSubmitDisabled}
                    onClick={
                      onAcknowledge ? handleSubmit(onAcknowledge) : undefined
                    }
                  >
                    {isLoading ? (
                      <FontAwesomeIcon
                        data-testid='loading-icon'
                        icon={faArrowsRotate}
                        className='animate-spin'
                      />
                    ) : (
                      'Acknowledge & Continue'
                    )}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AffiliatedBusinessArrangementModal;
